import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, XCircle, Loader2, Copy, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getCheckoutStatus } from '../lib/api';
import { useCart } from '../context/CartContext';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const data = await getCheckoutStatus(sessionId);
        setPaymentInfo(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          clearCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        attempts++;
        setTimeout(pollStatus, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    pollStatus();
  }, [sessionId, clearCart]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-[#F3F2EE] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Loader2 className="w-10 h-10 text-[#5C5C60] animate-spin" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold text-[#1A1A1C] font-['Manrope'] mb-4">
              Processing Payment...
            </h1>
            <p className="text-[#5C5C60]">
              Please wait while we confirm your payment.
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-[#34C759]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#34C759]" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold text-[#1A1A1C] font-['Manrope'] mb-4">
              Order Confirmed!
            </h1>
            <p className="text-[#5C5C60] mb-8 max-w-md mx-auto">
              Thank you for your purchase. Save your order number for tracking and returns.
            </p>

            {paymentInfo && (
              <div className="bg-[#FAFAFA] rounded-2xl p-8 max-w-sm mx-auto mb-8 space-y-5">
                {/* Order Number */}
                {paymentInfo.order_number && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-[#888] mb-1">Order Number</p>
                    <div className="flex items-center justify-center gap-2">
                      <p className="text-2xl font-bold text-[#1A1A1C] font-mono tracking-wide" data-testid="order-number">
                        {paymentInfo.order_number}
                      </p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(paymentInfo.order_number);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className="p-1.5 text-[#888] hover:text-[#1A1A1C] hover:bg-[#F0F0F0] rounded-lg transition-colors"
                        aria-label="Copy order number"
                        data-testid="copy-order-number"
                      >
                        {copied ? <Check className="w-4 h-4 text-[#34C759]" strokeWidth={2} /> : <Copy className="w-4 h-4" strokeWidth={1.5} />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="pt-4 border-t border-[#E8E8E8]">
                  <div className="flex items-center gap-3 justify-center mb-1">
                    <Package className="w-5 h-5 text-[#888]" strokeWidth={1.5} />
                    <span className="text-sm text-[#888]">Order Total</span>
                  </div>
                  <p className="text-3xl font-bold text-[#1A1A1C]">
                    ${(paymentInfo.amount_total / 100).toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/products')}
                className="bg-[#1A1A1C] text-white px-8 py-4 rounded-full font-medium flex items-center justify-center gap-2 hover:bg-[#333333] transition-all"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
                <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-white text-[#1A1A1C] border border-[#E5E5E5] px-8 py-4 rounded-full font-medium hover:bg-[#F3F2EE] transition-all"
              >
                Back to Home
              </button>
            </div>
          </div>
        );

      case 'expired':
      case 'error':
      case 'timeout':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-[#FF3B30]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-[#FF3B30]" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold text-[#1A1A1C] font-['Manrope'] mb-4">
              {status === 'timeout' ? 'Payment Status Unknown' : 'Payment Issue'}
            </h1>
            <p className="text-[#5C5C60] mb-8 max-w-md mx-auto">
              {status === 'timeout'
                ? 'We could not confirm your payment status. Please check your email for confirmation.'
                : 'There was an issue with your payment. Please try again or contact support.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/cart')}
                className="bg-[#1A1A1C] text-white px-8 py-4 rounded-full font-medium hover:bg-[#333333] transition-all"
                data-testid="return-to-cart-btn"
              >
                Return to Cart
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-white text-[#1A1A1C] border border-[#E5E5E5] px-8 py-4 rounded-full font-medium hover:bg-[#F3F2EE] transition-all"
              >
                Back to Home
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="checkout-success-page">
      <Header />

      <main className="pt-32 pb-24">
        <div className="max-w-2xl mx-auto px-6">
          {renderContent()}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
